import { DateTime } from 'luxon'
import { useIntl } from 'react-intl'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import {
  type BudgetIntervalType,
  BudgetIntervalTypes,
} from '@webapp/models/enums/BudgetInterval'
import messages from './messages'

const convertDateToObject = (date: string | Date, timeZone: TimezonesEnum) => {
  const dateTime =
    typeof date === 'string'
      ? DateTime.fromISO(date, { zone: timeZone })
      : DateTime.fromJSDate(date, { zone: timeZone })

  return dateTime.toObject()
}

export const useFormatBudgetPeriod = () => {
  const intl = useIntl()
  const formatDate = useFormatDate()

  return (
    startDateTime: string | Date,
    endDateTime: string | Date,
    period: Omit<BudgetIntervalType, 'none'>,
    timeZone: TimezonesEnum
  ): string => {
    const startDateObj = convertDateToObject(startDateTime, timeZone)

    const endDateObj = convertDateToObject(endDateTime, timeZone)

    switch (period) {
      case BudgetIntervalTypes.YEARLY:
        return DateTime.fromObject(startDateObj).toFormat('yyyy')
      case BudgetIntervalTypes.QUARTERLY:
        return intl.formatMessage(messages.quarterLabel, {
          quarter: DateTime.fromObject(startDateObj).toFormat('q'),
          year: DateTime.fromObject(startDateObj).toFormat('yyyy'),
        })
      case BudgetIntervalTypes.MONTHLY:
        return DateTime.fromObject(startDateObj).toFormat('MMMM yyyy')
      default:
        const {
          year: startYear,
          month: startMonth,
          day: startDay,
          hour: startHour,
          minute: startMinute,
          second: startSecond,
        } = startDateObj

        const {
          year: endYear,
          month: endMonth,
          day: endDay,
          hour: endHour,
          minute: endMinute,
          second: endSecond,
        } = endDateObj

        return `${formatDate(
          new Date(
            startYear,
            startMonth - 1,
            startDay,
            startHour,
            startMinute,
            startSecond
          ),
          {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }
        )} - ${formatDate(
          new Date(
            endYear,
            endMonth - 1,
            endDay,
            endHour,
            endMinute,
            endSecond
          ),
          {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }
        )}`
    }
  }
}
